import type {AxiosError} from "axios";

import {captureDomainEvent} from "./capture";
import {DEVICE_FAILURE_ERROR_CODES} from "./failure-domains";
import {normalizeHttpPathTag} from "./http-context";

export type ShiftCaptureContext = {
  operation: string;
  shiftId?: number;
  shiftBreakId?: number;
  gpsSource?: string;
};

type ShiftApiErrorBody = {
  error_code?: string;
  code?: string;
  message?: string;
};

/** Server rejected submitted coordinates on shift start (proximity / location policy). */
export function isShiftLocationRejectedError(error: unknown, operation?: string): boolean {
  if (!error || typeof error !== "object" || !("isAxiosError" in error)) return false;

  const axiosError = error as AxiosError<ShiftApiErrorBody>;
  const status = axiosError.response?.status;
  if (typeof status !== "number" || status < 400 || status >= 500) return false;

  const op = operation ?? axiosError.config?.meta?.operation;
  const path = normalizeHttpPathTag(axiosError.config?.url);
  const isShiftStart =
    op === "shift_start" || path === "shift/start" || path.endsWith("/shift/start");
  if (!isShiftStart) return false;

  const data = axiosError.response?.data;
  if (data?.code === DEVICE_FAILURE_ERROR_CODES.LOCATION_REQUIRED) return true;
  if (data?.error_code === DEVICE_FAILURE_ERROR_CODES.LOCATION_REQUIRED) return true;

  const message = (data?.message ?? "").toLowerCase();
  return (
    message.includes("location") &&
    (message.includes("required") ||
      message.includes("proximity") ||
      message.includes("distance") ||
      message.includes("range") ||
      message.includes("within"))
  );
}

export function captureShiftLocationRejected(
  error: unknown,
  context: ShiftCaptureContext,
): void {
  if (!isShiftLocationRejectedError(error, context.operation)) return;

  const axiosError =
    error && typeof error === "object" && "isAxiosError" in error
      ? (error as AxiosError)
      : undefined;

  captureDomainEvent({
    feature: "shifts",
    errorCode: DEVICE_FAILURE_ERROR_CODES.LOCATION_REQUIRED,
    failureDomain: "gps",
    operation: context.operation,
    message: "Shift start location rejected by server",
    axiosError,
    extra: {
      shift_id: context.shiftId,
      gps_source: context.gpsSource,
    },
  });
}

export function captureShiftDomainError(_error: unknown, _context: ShiftCaptureContext): void {
  // Expected shift/break business-rule responses are user-facing validation — not GlitchTip defects.
}
